import React from 'react';
import { Check, X, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { LogoSuggestion } from './useLogoSuggestion';

interface Props {
  suggestion: LogoSuggestion | null;
  checking: boolean;
  onApply: () => void;
  onDismiss: () => void;
}

/**
 * Inline "we found a logo — use it?" card, rendered beneath a company-URL input.
 * Pair with useLogoSuggestion: `lookup` on blur, then feed its state here.
 */
const LogoSuggestionCard: React.FC<Props> = ({ suggestion, checking, onApply, onDismiss }) => {
  const { t } = useTranslation();

  if (checking) {
    return (
      <p className="mt-2 text-xs text-gray-500 flex items-center">
        <Loader className="w-3 h-3 mr-1 animate-spin" />
        {t('common.companyLogo.checking')}
      </p>
    );
  }

  if (!suggestion) return null;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
      <img
        src={suggestion.url}
        alt={suggestion.hostname}
        className="w-10 h-10 rounded-lg border border-purple-200 object-contain bg-white flex-shrink-0"
      />
      <p className="text-sm text-purple-800 flex-1 basis-40 min-w-0 break-words">
        {t('common.companyLogo.logoFound', { hostname: suggestion.hostname })}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onApply}
          className="inline-flex items-center whitespace-nowrap px-3 py-1.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Check className="w-4 h-4 mr-1" />
          {t('common.companyLogo.useThisLogo')}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="inline-flex items-center whitespace-nowrap px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <X className="w-4 h-4 mr-1" />
          {t('common.companyLogo.dismiss')}
        </button>
      </div>
    </div>
  );
};

export default LogoSuggestionCard;
